import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useI18n, useLocalized } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { can, useSession } from '../lib/session';
import {
  useAssignWork, useBookingCount, useCancelCar, useFloor, useInspectionRules, useSetFloorVehicle,
  useSetWork, useAssignees,
} from '../lib/queries';
import {
  Alert, Button, Card, EmptyState, Field, Pill, SectionTitle, Select, Skeleton,
} from '../components/ui';
import { AddWork } from '../components/AddWork';
import { CameraIcon } from '../components/inspection/CameraIcon';
import { InspectionsSheet, PhotosSheet } from '../components/inspection/FloorInspection';
import { BookingList } from '../components/BookingList';
import { Deadline } from '../components/Deadline';
import type { FloorCar, FloorLine, WorkStatus } from '../lib/types';

const WORK_TONE: Record<WorkStatus, 'neutral' | 'warning' | 'positive'> = {
  PENDING: 'neutral',
  IN_PROGRESS: 'warning',
  DONE: 'positive',
};

/** For a car taken in without saying which one: the customer's cars, one tap each. */
function PickVehicle({ car }: { car: FloorCar }) {
  const { t } = useI18n();
  const errorText = useApiError();
  const setVehicle = useSetFloorVehicle(car.id);

  return (
    <div className="mt-3 rounded-xl border border-warning/30 bg-warning/5 p-3">
      <p className="text-xs text-ink-dim">{t('floor.whichCarQuestion')}</p>
      {setVehicle.error instanceof ApiError && (
        <div className="mt-2"><Alert>{errorText(setVehicle.error) ?? setVehicle.error.message}</Alert></div>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {car.vehicleChoices.map((vehicle) => (
          <button
            key={vehicle.id}
            disabled={setVehicle.isPending}
            onClick={() => setVehicle.mutate(vehicle.id)}
            className="rounded-xl border border-line bg-raised px-3.5 py-2 text-left text-sm text-ink transition hover:border-brand/60 disabled:opacity-50"
          >
            <span className="block font-medium">{vehicle.make} {vehicle.model}</span>
            {vehicle.licensePlate && (
              <span className="tabular block text-[0.7rem] text-ink-faint">{vehicle.licensePlate}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function Line({ line, car, mayAssign }: { line: FloorLine; car: FloorCar; mayAssign: boolean }) {
  const { t } = useI18n();
  const localized = useLocalized();
  const setWork = useSetWork();
  const assign = useAssignWork(car.id);
  const assignees = useAssignees(mayAssign);

  /*
   * Stacked on a phone. Side by side the service name was squeezed into a
   * sliver between the status pill and the name of whoever is doing it.
   */
  return (
    <div className="rounded-xl border border-line bg-raised px-3 py-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2">
      <div className="flex min-w-0 items-center gap-2 sm:flex-1">
        <span className="min-w-0 flex-1 text-sm text-ink">
          {localized(line.name)}
          {line.quantity > 1 && <span className="tabular text-ink-faint"> × {line.quantity}</span>}
        </span>
        <Pill tone={WORK_TONE[line.workStatus]}>{t(`work.status.${line.workStatus}`)}</Pill>
      </div>

      <div className="mt-2 flex items-center gap-2 sm:mt-0 [&>div]:flex-1 sm:[&>div]:flex-none">
      {mayAssign ? (
        <Select
          // Labelled on a phone, where the list sits on its own line and a
          // bare name reads as a caption rather than something to change.
          label={t('floor.who')}
          value={line.performedByAdminId ?? ''}
          disabled={line.workStatus === 'DONE'}
          onChange={(event) => assign.mutate({
            itemId: line.id,
            performedByAdminId: event.target.value || null,
          })}
          className="w-full sm:w-auto sm:min-w-36"
          labelClassName="sm:hidden"
        >
          <option value="">{t('floor.unassigned')}</option>
          {assignees(line.performedByAdminId).map((person) => (
            <option key={person.id} value={person.id}>
              {person.firstName} {person.lastName}
            </option>
          ))}
        </Select>
      ) : (
        <span className="text-xs text-ink-faint">
          {line.performedByName ?? t('floor.unassigned')}
        </span>
      )}

      {mayAssign && line.workStatus === 'DONE' && (
        <Button
          variant="quiet"
          size="sm"
          loading={setWork.isPending}
          onClick={() => setWork.mutate({ itemId: line.id, status: 'IN_PROGRESS' })}
        >
          {t('floor.reopen')}
        </Button>
      )}
      </div>
    </div>
  );
}

function Car({ car }: { car: FloorCar }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const cancel = useCancelCar();
  const errorText = useApiError();

  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [handover, setHandover] = useState<'inspect' | 'inspectThenHandOver' | null>(null);
  const [intakeLater, setIntakeLater] = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);
  const { data: rules } = useInspectionRules();

  const mayAssign = can(session, 'orders.assign');
  const mayCancel = can(session, 'orders.cancel');
  const mayComplete = can(session, 'orders.complete');
  const mayAdd = can(session, 'orders.accept');

  const vehicle = [car.vehicleMake, car.vehicleModel].filter(Boolean).join(' ');
  const done = car.lines.filter((l) => l.workStatus === 'DONE').length;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {car.vehicleId ? (
              <Link
                to={`/vehicles/${car.vehicleId}`}
                className="font-display text-lg font-bold leading-tight text-ink transition hover:text-brand"
              >
                {vehicle}
              </Link>
            ) : (
              <span className="font-display text-lg font-bold leading-tight text-ink">{t('floor.noVehicle')}</span>
            )}
            {car.vehiclePlate && (
              <span className="tabular rounded-md border border-line px-1.5 py-0.5 text-xs text-ink-dim">
                {car.vehiclePlate}
              </span>
            )}
            {car.status === 'READY' && <Pill tone="positive">{t('floor.ready')}</Pill>}
          </div>
          <Link
            to={`/customers/${car.customerId}`}
            className="mt-1 block text-sm text-ink-dim transition hover:text-brand"
          >
            {car.customerName}
          </Link>
        </div>

        <div className="text-right">
          <span className="tabular block text-[0.7rem] text-ink-faint">{car.reference}</span>
          <Deadline orderId={car.id} promisedAt={car.promisedAt} mayEdit={mayAdd} />
          <span className="tabular mt-0.5 block text-[0.7rem] text-ink-faint">
            {t('floor.doneOf', { done, total: car.lines.length })}
          </span>
        </div>
      </div>

      {!vehicle && car.vehicleChoices.length > 0 && mayAdd && <PickVehicle car={car} />}

      {car.notes && (
        <p className="mt-3 border-l-2 border-brand/40 pl-3 text-xs leading-relaxed text-ink-dim">
          {car.notes}
        </p>
      )}

      <div className="mt-3 space-y-2">
        {car.lines.map((line) => (
          <Line key={line.id} line={line} car={car} mayAssign={mayAssign} />
        ))}
      </div>

      {adding ? (
        <AddWork orderId={car.id} mayAssign={mayAssign} onDone={() => setAdding(false)} />
      ) : mayAdd && (
        <button
          onClick={() => setAdding(true)}
          className="mt-2 w-full rounded-xl border border-dashed border-line px-3 py-2.5 text-sm text-ink-dim transition hover:border-brand/50 hover:text-ink"
        >
          + {t('floor.addWork')}
        </button>
      )}

      {(car.inspected.intake || car.inspected.handover || (mayAdd && car.vehicleId)) && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {(car.inspected.intake || car.inspected.handover) ? (
            <button
              onClick={() => setShowPhotos(true)}
              className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-line bg-raised px-2 text-center text-xs font-medium leading-tight text-ink-dim transition hover:border-ink-faint hover:text-ink"
            >
              <CameraIcon className="h-4 w-4 shrink-0" />
              {t('inspection.intakeDone')}
            </button>
          ) : mayAdd && car.vehicleId ? (
            // Taken in with the photos left for later: what is missing stands out until it is done.
            <button
              onClick={() => setIntakeLater(true)}
              className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-dashed border-warning/60 bg-warning/10 px-2 text-center text-xs font-semibold leading-tight text-warning transition hover:border-warning"
            >
              <CameraIcon className="h-4 w-4 shrink-0" />
              {t('inspection.intakeMissing')}
            </button>
          ) : <span />}
          {car.inspected.handover ? (
            <span className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-positive/40 bg-positive/10 px-2 text-center text-xs font-medium leading-tight text-positive">
              ✓ {t('inspection.handoverDone')}
            </span>
          ) : mayAdd && car.vehicleId ? (
            <button
              onClick={() => setHandover('inspect')}
              className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-dashed border-brand/50 px-2 text-center text-xs font-semibold leading-tight text-brand transition hover:border-brand"
            >
              <CameraIcon className="h-4 w-4 shrink-0" />
              {t('inspection.startHandover')}
            </button>
          ) : <span />}
        </div>
      )}

      {cancel.error instanceof ApiError && (
        <div className="mt-3"><Alert>{errorText(cancel.error) ?? cancel.error.message}</Alert></div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {mayComplete && (
          <Button
            // Pricing happens at the till, so handing over just carries the
            // car across to the order screen with its lines already filled in.
            className="flex-1"
            variant={car.status === 'READY' ? 'primary' : 'ghost'}
            onClick={() => (
              // Photographed before it leaves, when the studio requires it; by
              // someone else later if whoever is handing over cannot photograph.
              rules?.handoverRequired && !car.inspected.handover && car.vehicleId
                ? (mayAdd
                  ? setHandover('inspectThenHandOver')
                  : navigate(`/orders/new?customer=${car.customerId}&accepted=${car.id}&handoverLater=1`))
                : navigate(`/orders/new?customer=${car.customerId}&accepted=${car.id}`)
            )}
          >
            {t('floor.handOver')}
          </Button>
        )}
        {mayCancel && !cancelling && (
          <Button variant="quiet" onClick={() => setCancelling(true)}>{t('floor.cancel')}</Button>
        )}
      </div>

      {cancelling && (
        <div className="mt-3 space-y-2">
          <Field
            label={t('floor.cancelReason')}
            value={reason}
            autoFocus
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              variant="danger"
              className="flex-1"
              disabled={reason.trim().length < 3}
              loading={cancel.isPending}
              onClick={() => cancel.mutate({ id: car.id, reason: reason.trim() }, {
                onSuccess: () => { setCancelling(false); setReason(''); },
              })}
            >
              {t('floor.cancelConfirm')}
            </Button>
            <Button variant="ghost" onClick={() => { setCancelling(false); setReason(''); }}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
      {handover && (
        <PhotosSheet
          orderId={car.id}
          kind="HANDOVER"
          thenHandOver={handover === 'inspectThenHandOver'}
          onClose={() => setHandover(null)}
          onDone={() => {
            const next = handover;
            setHandover(null);
            if (next === 'inspectThenHandOver') navigate(`/orders/new?customer=${car.customerId}&accepted=${car.id}`);
          }}
          onLater={handover === 'inspectThenHandOver'
            ? () => navigate(`/orders/new?customer=${car.customerId}&accepted=${car.id}&handoverLater=1`)
            : undefined}
        />
      )}
      {intakeLater && car.vehicleId && (
        <PhotosSheet
          orderId={car.id}
          kind="INTAKE"
          vehicle={{ id: car.vehicleId, customerId: car.customerId, vin: car.vehicleVin }}
          onClose={() => setIntakeLater(false)}
          onDone={() => setIntakeLater(false)}
        />
      )}
      {showPhotos && <InspectionsSheet orderId={car.id} onClose={() => setShowPhotos(false)} />}
    </Card>
  );
}

export function FloorPage() {
  const { t } = useI18n();
  const { data: session } = useSession();
  const { data, isPending, error } = useFloor();
  const { data: expected } = useBookingCount(can(session, 'bookings.read'));
  const [params, setParams] = useSearchParams();

  const tab = params.get('tab') === 'expected' ? 'expected' : 'here';
  const cars = data?.items ?? [];
  const ready = cars.filter((c) => c.status === 'READY');
  const working = cars.filter((c) => c.status !== 'READY');

  return (
    <div className="mx-auto max-w-4xl pb-16">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">
            {t('floor.title')}
          </h1>
          <p className="mt-1.5 text-sm text-ink-dim">
            {t('floor.subtitle', { count: cars.length })}
          </p>
        </div>
        {can(session, 'orders.accept') && tab === 'here' && (
          <Link
            to="/floor/accept"
            className="inline-flex min-h-11 items-center rounded-xl bg-brand px-4 text-sm font-semibold text-black transition hover:bg-brand-soft"
          >
            {t('floor.accept')}
          </Link>
        )}
      </header>

      {can(session, 'bookings.read') && (
        <div className="mb-5 flex gap-1 rounded-xl border border-line p-1">
          {([['here', cars.length], ['expected', expected?.today ?? 0]] as const).map(([key, count]) => (
            <button
              key={key}
              onClick={() => setParams(key === 'here' ? {} : { tab: key })}
              aria-pressed={tab === key}
              className={`flex min-h-10 flex-1 items-center justify-center gap-2 rounded-lg text-sm transition-colors ${
                tab === key ? 'bg-raised font-semibold text-ink' : 'text-ink-dim hover:text-ink'
              }`}
            >
              {t(key === 'here' ? 'floor.tabHere' : 'floor.tabExpected')}
              {count > 0 && <Pill tone={key === 'expected' ? 'warning' : 'neutral'}>{count}</Pill>}
            </button>
          ))}
        </div>
      )}

      {tab === 'expected' && <BookingList />}

      {tab === 'here' && error && <div className="mb-4"><Alert>{t('app.apiUnreachable')}</Alert></div>}

      {tab === 'here' && (isPending ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-48" />)}</div>
      ) : cars.length === 0 ? (
        <EmptyState title={t('floor.empty')} />
      ) : (
        <div className="space-y-5">
          {ready.length > 0 && (
            <section>
              <SectionTitle>{t('floor.readyGroup')}</SectionTitle>
              <div className="space-y-3">
                {ready.map((car) => <Car key={car.id} car={car} />)}
              </div>
            </section>
          )}
          {working.length > 0 && (
            <section>
              <SectionTitle>{t('floor.workingGroup')}</SectionTitle>
              <div className="space-y-3">
                {working.map((car) => <Car key={car.id} car={car} />)}
              </div>
            </section>
          )}
        </div>
      ))}
    </div>
  );
}
